import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {fetchBoardList} from "../api/boardManage";
import {globalDialogTipsProps, GuestMode, ImagePricingRule, memberState, PendingTask, UnreadTask} from "../types/member.type";
import {getMemberInfo} from "../api/payment";
import {getImagePricingRules, getNotifyUnreadCount} from "../api/common";
import {getLocalStorage} from "../util/storage";
import {LoginProps} from "../types/authority";

export const fetchMemberInfoAsync = createAsyncThunk(
    'member/info',
    async () => {
        const useInfoStr = getLocalStorage('userInfo');
        const localUserInfo = useInfoStr ? JSON.parse(useInfoStr) as LoginProps : null;
        if (!localUserInfo) return null;
        return await getMemberInfo(localUserInfo?.email);
    }
);

export const fetchNotifyUnreadCountAsync = createAsyncThunk(
    'member/notifyUnreadCount',
    async () => {
        const useInfoStr = getLocalStorage('userInfo');
        const localUserInfo = useInfoStr ? JSON.parse(useInfoStr) as LoginProps : null;
        if (!localUserInfo) return 0;
        return await getNotifyUnreadCount();
    }
);

export const fetchImagePricingRulesAsync = createAsyncThunk(
    'member/imagePricingRules',
    async (): Promise<ImagePricingRule[]> => {
        return await getImagePricingRules();
    }
);

const initialState: memberState = {
    memberInfo: null,
    imagePricingRules: [],
    GuestMode: 'owner',
    openDropDialog: false,
    openMembershipPurchaseDialog: false,
    openYearMemberDialog: false,
    openMemberBillDialog: false,
    openWaterShortage: false,
    openWaterShortage2: false,
    globalDialogTips: null,
    notifyDialogTips: false,
    notifyUnreadCount: 0,
    dropActiveDialogOpen: false,
    surveyDialogOpen: false,
    pendingLoginData: null,
    pendingTasks: [],
    unreadTasks: []
};

export const memberSlice = createSlice({
    name: 'member',
    initialState,
    reducers: {
        setChangeGuestMode: (state, action: PayloadAction<GuestMode>) => {
            state.GuestMode = action.payload;
        },
        setOpenDropDialogChange: (state, action: PayloadAction<boolean>) => {
            state.openDropDialog = action.payload;
        },
        setOpenMembershipPurchaseDialogChange: (state, action: PayloadAction<boolean>) => {
            state.openMembershipPurchaseDialog = action.payload;
        },
        setOpenYearMemberDialogChange: (state, action: PayloadAction<boolean>) => {
            state.openYearMemberDialog = action.payload;
        },
        setOpenMemberBillDialogChange: (state, action: PayloadAction<boolean>) => {
            state.openMemberBillDialog = action.payload;
        },
        setDropActiveDialogOpen: (state, action: PayloadAction<boolean>) => {
            state.dropActiveDialogOpen = action.payload;
        },
        setSurveyDialogOpen: (state, action: PayloadAction<boolean>) => {
            state.surveyDialogOpen = action.payload;
        },
        setPendingLoginData: (state, action: PayloadAction<any>) => {
            state.pendingLoginData = action.payload;
        },
        setNotifyDialogTipsChange: (state, action: PayloadAction<boolean>) => {
            state.notifyDialogTips = action.payload;
        },
        setOpenWaterShortage: (state, action: PayloadAction<boolean>) => {
            state.openWaterShortage = action.payload;
        },
        setOpenWaterShortage2: (state, action: PayloadAction<boolean>) => {
            state.openWaterShortage2 = action.payload;
        },
        setGlobalDialogTips: (state, action: PayloadAction<globalDialogTipsProps | null>) => {
            state.globalDialogTips = action.payload;
        },
        setPendingTasks: (state, action: PayloadAction<PendingTask[]>) => {
            state.pendingTasks = action.payload;
        },
        setUnreadTasks: (state, action: PayloadAction<UnreadTask[]>) => {
            state.unreadTasks = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMemberInfoAsync.pending, (state) => {
                console.log('Fetching board list...');
            })
            .addCase(fetchMemberInfoAsync.fulfilled, (state, action) => {
                state.memberInfo = action.payload;
            })
            .addCase(fetchMemberInfoAsync.rejected, (state, action) => {
                console.error('Failed to fetch board list:', action.error);
            })
            .addCase(fetchNotifyUnreadCountAsync.fulfilled, (state, action) => {
                state.notifyUnreadCount = action.payload ?? 0;
            })
            .addCase(fetchImagePricingRulesAsync.fulfilled, (state, action) => {
                state.imagePricingRules = action.payload ?? [];
            });
    },
});

export const {
    setChangeGuestMode,
    setOpenDropDialogChange,
    setOpenMembershipPurchaseDialogChange,
    setOpenYearMemberDialogChange,
    setOpenMemberBillDialogChange,
    setOpenWaterShortage,
    setGlobalDialogTips,
    setNotifyDialogTipsChange,
    setOpenWaterShortage2,
    setDropActiveDialogOpen,
    setSurveyDialogOpen,
    setPendingLoginData,
    setPendingTasks,
    setUnreadTasks
} = memberSlice.actions;

export default memberSlice.reducer;
